import React from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVGrid, EVTypography, EVLinearProgress,
} from 'lib/components';
import clsx from 'lib/clsx';

function SitemapSubmittedSummary({
  classes,
  orderPlacedDisplay,
  address,
  estimatedCost,
  consumedUnits,
  usage,
  imageUrl,
  imageError,
  showEstimatedCost,
  showUsage,
}) {
  const usagePercent = usage?.totalUnits
    ? Math.round((usage.usedUnits / usage.totalUnits) * 100)
    : 0;
  // Color bands: 0–69 ocean (default), 70–99 mango, 100+ marigold (order-submitted uses ocean + 100%+ only)
  const usageProgressClass = clsx(
    classes.usageProgress,
    usagePercent >= 100 && classes.usageProgressFull,
    usagePercent >= 70 && usagePercent < 100 && classes.usageProgressHigh,
  );

  return (
    <EVGrid className={classes.summaryRow}>
      <EVGrid className={classes.summaryMeta}>
        <EVGrid className={classes.metaItem}>
          <EVTypography className={classes.metaLabel}>Order placed</EVTypography>
          <EVTypography className={classes.metaValue}>{orderPlacedDisplay}</EVTypography>
        </EVGrid>

        <EVGrid className={classes.metaItem}>
          <EVTypography className={classes.metaLabel}>Address</EVTypography>
          <EVTypography className={classes.metaValue}>{address}</EVTypography>
        </EVGrid>

        {showEstimatedCost && (
          <EVGrid className={classes.metaItem}>
            <EVTypography className={classes.metaLabel}>Estimated cost</EVTypography>
            <EVTypography className={clsx(classes.metaValue, classes.metaValueBold)}>
              {estimatedCost}
            </EVTypography>
          </EVGrid>
        )}

        {/* Authorized subscription users only — usage from subscription dashboard APIs */}
        {showEstimatedCost && showUsage && (
          <EVGrid className={classes.metaItem}>
            <EVTypography className={classes.metaLabel}>Usage</EVTypography>
            <EVGrid className={classes.costColumn}>
              <span className={classes.unitBadge}>
                {consumedUnits}
                {' '}
                Unit
                {consumedUnits === 1 ? '' : 's'}
              </span>
              {usage?.totalUnits > 0 && (
                <EVGrid className={classes.usageRoot}>
                  <EVGrid className={classes.usagePlanRow}>
                    <EVTypography className={classes.usagePlanName}>
                      {usage.planName}
                    </EVTypography>
                    <EVTypography className={classes.usagePlanCount}>
                      {usage.usedUnits}
                      {' / '}
                      {usage.totalUnits}
                      {' units (includes pending orders)'}
                    </EVTypography>
                  </EVGrid>
                  <EVLinearProgress
                    variant="determinate"
                    value={Math.min(usagePercent, 100)}
                    className={usageProgressClass}
                  />
                  <EVTypography className={classes.usageCaption}>
                    {usagePercent}
                    % Usage
                  </EVTypography>
                </EVGrid>
              )}
            </EVGrid>
          </EVGrid>
        )}
      </EVGrid>

      <EVGrid className={classes.imageColumn}>
        <EVGrid className={classes.imageContainer}>
          {imageError && (
            <EVTypography className={classes.imageError}>{imageError}</EVTypography>
          )}
          {imageUrl && !imageError && (
            <img src={imageUrl} alt="Sitemap" className={classes.image} />
          )}
        </EVGrid>
      </EVGrid>
    </EVGrid>
  );
}

SitemapSubmittedSummary.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  orderPlacedDisplay: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  estimatedCost: PropTypes.string.isRequired,
  consumedUnits: PropTypes.number.isRequired,
  usage: PropTypes.shape({
    planName: PropTypes.string,
    usedUnits: PropTypes.number,
    totalUnits: PropTypes.number,
  }),
  imageUrl: PropTypes.string,
  imageError: PropTypes.string,
  showEstimatedCost: PropTypes.bool,
  showUsage: PropTypes.bool,
};

SitemapSubmittedSummary.defaultProps = {
  usage: null,
  imageUrl: null,
  imageError: null,
  showEstimatedCost: true,
  showUsage: false,
};

export default SitemapSubmittedSummary;
